import React, { useEffect, useState } from 'react';
import API from 'shared/api';
import { FiShoppingBag, FiDollarSign, FiCreditCard, FiTrendingDown } from 'react-icons/fi';
import { COL } from 'shared/darkColors';
import {
  S, money, intFmt, Kpi, SectionCard, Cargando, ErrorMsg, buildQuery, descargarCSV,
} from './reportesCommon';

export default function ReporteCompras({ filtros }) {
  const [resumen, setResumen] = useState(null);
  const [porProveedor, setPorProveedor] = useState([]);
  const [aging, setAging] = useState(null);
  const [historial, setHistorial] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(false);

    Promise.all([
      API.get(`/compras/reporte?${buildQuery(filtros).toString()}`)
        .then((r) => r.data.status === 'success' && r.data.data)
        .catch(() => null),
      API.get('/compras/historial-costos', { params: { limite: 100 } })
        .then((r) => r.data.status === 'success' && r.data.data)
        .catch(() => null),
    ])
      .then(([rep, hist]) => {
        if (!alive) return;
        setResumen(rep?.resumen || null);
        setPorProveedor(rep?.por_proveedor || []);
        setAging(rep?.aging || null);
        setHistorial(hist || []);
      })
      .catch(() => alive && setError(true))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtros.sede, filtros.fechaDesde, filtros.fechaHasta]);

  if (loading) return <Cargando />;
  if (error) return <ErrorMsg />;

  const exportar = () => {
    descargarCSV(
      'reporte_compras.csv',
      ['Proveedor', 'Compras', 'Total', 'Pendiente'],
      porProveedor.map((p) => [p.proveedor, p.n_compras, money(p.total), money(p.pendiente)])
    );
  };

  return (
    <div>
      <div style={S.kpiGrid}>
        <Kpi label="Compras" value={intFmt(resumen?.n_compras)} icon={FiShoppingBag} color={COL.pri2} />
        <Kpi label="Total comprado" value={money(resumen?.total)} icon={FiDollarSign} color={COL.green} />
        <Kpi label="Saldo pendiente" value={money(resumen?.pendiente)} icon={FiCreditCard} color={COL.amber} />
      </div>

      {aging && (
        <SectionCard title="Aging de cuentas por pagar" icon={FiCreditCard} color={COL.amber}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: '10px' }}>
            {[
              { label: 'Corriente', v: aging.corriente, c: COL.green },
              { label: 'Vencidas 1-30', v: aging.vencidas_30, c: COL.amber },
              { label: 'Vencidas 31-60', v: aging.vencidas_60, c: COL.orange },
              { label: 'Vencidas 61+', v: aging.vencidas_90, c: COL.red },
            ].map((b) => (
              <div key={b.label} style={{ padding: '10px 12px', borderRadius: '8px', background: 'var(--agx-bg2)', border: `1px solid ${COL.border}` }}>
                <div style={{ fontSize: '11px', color: COL.muted }}>{b.label}</div>
                <div style={{ fontSize: '16px', fontWeight: 800, color: b.c }}>{money(b.v)}</div>
              </div>
            ))}
          </div>
        </SectionCard>
      )}

      <SectionCard title="Compras por proveedor" icon={FiShoppingBag} color={COL.pri2} hint="Según el período seleccionado">
        {porProveedor.length === 0 ? (
          <div style={{ textAlign: 'center', color: COL.muted, padding: '16px', fontSize: '13px' }}>
            Sin compras en el período
          </div>
        ) : (
          <div style={S.tableWrap}>
            <table style={S.table}>
              <thead>
                <tr>
                  <th style={S.th}>Proveedor</th>
                  <th style={{ ...S.th, textAlign: 'right' }}>Compras</th>
                  <th style={{ ...S.th, textAlign: 'right' }}>Total</th>
                  <th style={{ ...S.th, textAlign: 'right' }}>Pendiente</th>
                </tr>
              </thead>
              <tbody>
                {porProveedor.map((p, i) => (
                  <tr key={i}>
                    <td style={S.td}>{p.proveedor}</td>
                    <td style={{ ...S.td, textAlign: 'right' }}>{intFmt(p.n_compras)}</td>
                    <td style={{ ...S.td, textAlign: 'right', fontWeight: 700 }}>{money(p.total)}</td>
                    <td style={{ ...S.td, textAlign: 'right', color: p.pendiente > 0 ? COL.red : COL.muted }}>{money(p.pendiente)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div style={{ marginTop: '10px', display: 'flex', justifyContent: 'flex-end' }}>
          <button style={S.btn} onClick={exportar}>Exportar CSV</button>
        </div>
      </SectionCard>

      <SectionCard title="Historial de costos reciente" icon={FiTrendingDown} color={COL.orange} hint="Últimos cambios de costo en productos e insumos">
        {historial.length === 0 ? (
          <div style={{ textAlign: 'center', color: COL.muted, padding: '16px', fontSize: '13px' }}>
            Sin cambios de costo registrados
          </div>
        ) : (
          <div style={S.tableWrap}>
            <table style={S.table}>
              <thead>
                <tr>
                  <th style={S.th}>Tipo</th>
                  <th style={S.th}>Código</th>
                  <th style={S.th}>Nombre</th>
                  <th style={{ ...S.th, textAlign: 'right' }}>Costo anterior</th>
                  <th style={{ ...S.th, textAlign: 'right' }}>Costo nuevo</th>
                  <th style={{ ...S.th, textAlign: 'right' }}>Fecha</th>
                </tr>
              </thead>
              <tbody>
                {historial.map((h) => (
                  <tr key={`${h.tipo}-${h.id}`}>
                    <td style={S.td}>
                      <span style={{ ...S.chip, background: h.tipo === 'producto' ? 'var(--agx-blue-bg)' : 'var(--agx-green-bg)', color: h.tipo === 'producto' ? COL.pri2 : COL.green }}>
                        {h.tipo === 'producto' ? 'Producto' : 'Insumo'}
                      </span>
                    </td>
                    <td style={S.td}>{h.codigo}</td>
                    <td style={S.td}>{h.nombre || '—'}</td>
                    <td style={{ ...S.td, textAlign: 'right' }}>{money(h.costo_anterior)}</td>
                    <td style={{ ...S.td, textAlign: 'right', fontWeight: 700 }}>{money(h.costo_nuevo)}</td>
                    <td style={{ ...S.td, textAlign: 'right' }}>{String(h.creado_en).slice(0, 10)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </SectionCard>
    </div>
  );
}
